import axios from 'axios';


const API_URL = '/public/';

class PublicService {

    /**
     * Fetches a module based on the provided base identifier.
     *
     * @param {string} base - The base identifier for the module.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the module data if successful, or false if an error occurs or the base is invalid.
     */
    async getModule(base) {
        if (!base || base == '') return false;
        try {
            let res = await axios.get(API_URL + 'module/get.php?base=' + base);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async fullLoadModule(base, override_cache_age = null) {
        if (!base || base == '') return false;
        let override_cache_age_param = '';
        if (override_cache_age) {
            override_cache_age_param = '&override_cache_age=' + override_cache_age;
        }
        try {
            let res = await axios.get(API_URL + 'module/load.php?base=' + base + override_cache_age_param);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches courses for a given module.
     *
     * @param {string} id_module - The ID of the module to fetch courses for.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the course data if successful, or false if an error occurs or the id_module is invalid.
     */
    async getCourses(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'courses/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches the packs and courses for a given module.
     *
     * @param {string} id_module - The ID of the module.
     * @returns {Promise<Object|boolean>} The data from the response if successful, otherwise false.
     */
    async getPacksCourses(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'packs_courses/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches options for a given module ID.
     *
     * @param {string} id_module - The ID of the module to fetch options for.
     * @returns {Promise<Object|boolean>} A promise that resolves to the options data if successful, or false if an error occurs or the id_module is invalid.
     */
    async getOptions(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'options/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches activities for a given module.
     *
     * @param {string} id_module - The ID of the module to fetch activities for.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the activities data if successful, or false if an error occurs or the id_module is invalid.
     */
    async getActivites(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'activites/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getOptionsForChampFormulaire(id, value) {
        if (!id || id == '') return false;
        if (!value) value = '';
        const params = {
            id: id,
            selected_value: value
        }
        try {
            let res = await axios.post(API_URL + 'champ_formulaire/get_options.php', params);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getChampSuggestions(id_module, nom, search = '') {
        if (!id_module || id_module == '') return [];
        if (!nom || nom == '') return [];
        try {
            const params = new URLSearchParams({ id_module, nom, search });
            let res = await axios.get(API_URL + 'champ_formulaire/get_suggestions.php?' + params);
            return res.data ?? [];
        }
        catch (err) {
            return [];
        }
    }

    /**
     * Fetches articles from the boutique based on the provided module ID.
     *
     * @param {string} id_module - The ID of the module to fetch articles for.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the data of the articles if successful, or false if an error occurs or the id_module is invalid.
     */
    async getArticlesBoutique(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'articles/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches metadata for a given resource type and resource ID.
     *
     * @param {string} resource_type - The type of the resource.
     * @param {string} id_resource - The ID of the resource.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the metadata object if successful, or false if an error occurs or parameters are invalid.
     */
    async getMetadatas(resource_type, id_resource) {
        if (!resource_type || resource_type == '' || !id_resource || id_resource == '') return false;
        try {
            let res = await axios.get(API_URL + 'metadatas/get.php?resource_type=' + resource_type + '&id_resource=' + id_resource);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches the VIP link for a given module.
     *
     * @param {string} id_module - The ID of the module.
     * @param {string} token - The authentication token.
     * @returns {Promise<Object|boolean>} The response data if successful, otherwise false.
     */
    async getLienVIP(id_module, token) {
        if (!id_module || id_module == '' || !token || token == '') return false;
        try {
            let res = await axios.get(API_URL + 'lien_vip/get.php?id_module=' + id_module + '&token=' + token);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getLienVIPMultiUsage(id_module, token) {
        if (!id_module || id_module == '' || !token || token == '') return false;
        try {
            let res = await axios.get(API_URL + 'lien_vip_multi_usage/get.php?id_module=' + id_module + '&token=' + token);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }
    /**
     * Fetches form fields for a given module.
     *
     * @param {string} id_module - The ID of the module to fetch form fields for.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the form fields data or false if an error occurs or the id_module is invalid.
     */
    async getChampsFormulaire(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'champs_formulaire/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getCodeReduction(nom, id_module, override_validity = false) {
        if (!nom || nom == '' || !id_module || id_module == '') return false;
        let override_validity_param = '';
        if (override_validity) {
            override_validity_param = '&override_validity=1';
        }
        try {
            let res = await axios.get(API_URL + 'code_reduction/get.php?nom=' + nom + '&id_module=' + id_module + override_validity_param);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async removeCodeReductionUsage(id) {
        if (!id) return false;
        try {
            await axios.get(API_URL + 'code_reduction/remove_usage.php?id=' + id);
            return true;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Checks the availability of a booking.
     *
     * @param {Object} booking - The booking details to check availability for.
     * @returns {Promise<Object|boolean>} - Returns the availability data if successful, otherwise returns false.
     */
    async checkCommandeAvailabilities(booking, groupe) {
        if (!booking) return false;
        booking.groupe = groupe;
        try {
            let res = await axios.post(API_URL + 'commande/check_availabilities.php', booking);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async checkIncoherence(nom, prenom, sexe) {
        try {
            let res = await axios.post(API_URL + 'incoherence/get.php', { nom: nom, prenom: prenom, sexe: sexe });
            return res.data;
        }
        catch (err) {
            return { "nom_valide": true, "prenom_valide": true, "sexe_valide": true };
        }
    }

    /**
     * Récupère les items disponibles dans la bourse aux dossards pour un module donné
     * 
     * @param {string} id_module - L'ID du module
     * @returns {Promise<Object|boolean>} - Les données de la bourse aux dossards ou false en cas d'erreur
     */
    async getBourseDossardsItems(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'bourse_dossards/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getDossardBourse(id_dossard_bourse) {
        if (!id_dossard_bourse || id_dossard_bourse == '') return false;
        try {
            let res = await axios.get(API_URL + 'bourse_dossard/get.php?id_dossard_bourse=' + id_dossard_bourse);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getPreInscriptions(params) {

        try {
            let res = await axios.get(API_URL + 'pre_inscriptions/get.php?' + params);
            return res.data;
        }

        catch (err) {
            return false;
        }
    }

    async getInscriptions(params) {
        try {
            let res = await axios.get(API_URL + 'inscriptions/get.php?' + params);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async checkDossardBourseNotInAnotherCart(dossard_bourse) {
        const update_dossard_bourse = await this.getDossardBourse(dossard_bourse.id);
        if (!update_dossard_bourse) return { checked: false, errors: ["Une erreur est survenue"] };
        if (update_dossard_bourse.status == 'PENDING_PAYMENT') return { checked: false, errors: ["Ce dossard est déjà en attente de paiement dans le panier d'un autre utilisateur."] };
        if (update_dossard_bourse.status == 'PAID') return { checked: false, errors: ["Ce dossard vient d'être acheté par un autre utilisateur."] };
        return { checked: true, errors: [] };
    }

    async getGroupeInfos(token_groupe, id_module) {
        if (!token_groupe || token_groupe == '' || !id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'lien_groupe/get.php?token=' + token_groupe + '&id_module=' + id_module);
            return res.data;
        }

        catch (err) {
            return false;
        }
    }
    async getListeAttenteByLatoken(latoken, id_module) {
        if (!latoken || latoken == '' || !id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'bourse_dossards/liste_attente/get_by_latoken.php?latoken=' + encodeURIComponent(latoken) + '&id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async verifyListeAttente(email, id_course, id_module, add_to_list) {
        if (!email || email == '' || !id_course || id_course == '' || !id_module || id_module == '') return false;
        let data = {
            mail: email,
            id_course: id_course,
            id_module: id_module,
            add_to_list: add_to_list
        }
        try {
            let res = await axios.post(API_URL + 'bourse_dossards/liste_attente/verify.php', data);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getBourseDossardsItemByToken(id_module, token) {
        if (!id_module || id_module == '' || !token || token == '') return false;
        try {
            let res = await axios.get(API_URL + 'bourse_dossard/get_by_token.php?id_module=' + id_module + '&token=' + token);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getSimpleInscriptionsByGroupe(token_groupe, id_module) {
        if (!token_groupe || token_groupe == '' || !id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'lien_groupe/inscriptions/get.php?token_groupe=' + token_groupe + '&id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Récupère les données de suivi d'un lien groupe (infos groupe + inscriptions).
     * Endpoint public - pas d'authentification requise.
     * Appelle lien_groupe/suivi/get.php 
     *
     * @param {string} token - Le token du lien groupe.
     * @param {string} id_module - L'ID du module.
     * @returns {Promise<Object|boolean>} - Les données de suivi (groupe + inscriptions) ou false en cas d'erreur.
     */
    async getSuiviLienGroupe(token, id_module) {
        if (!token || token == '' || !id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'lien_groupe/suivi/get.php?token=' + token + '&id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getListCreneaux(id_module, id_course = null) {
        if (!id_module || id_module == '') return false;
        let params = '?id_module=' + id_module;
        if (id_course) {
            params += '&id_course=' + id_course;
        }
        try {
            let res = await axios.get(API_URL + 'creneaux_retrait_dossard/get.php' + params);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Infos du chef d’équipe (relais) pour le retrait de dossard : nom/prénom et créneau choisi côté capitaine.
     * Réponse attendue : { nom, prenom, id_creneau_dossard? | null, libelle_creneau? } ou { error }.
     *
     * @param {{ id_module: string|number,  id_inscription?: string|number }} params
     * @returns {Promise<Object|boolean>}
     */
    async getLicenceExperiencePrice(id_course, type_licence_experience = "", id_federation = null) {
        try {
            const payload = {
                id_course,
                type_licence_experience,
            };
            const fed = parseInt(id_federation, 10);
            if (!isNaN(fed) && fed > 0) {
                payload.id_federation = fed;
            }
            let res = await axios.post("/webservice_FFTRI/get_licence_experience_price.php", payload);
            return res.data;
        } catch (err) {
            return false;
        }
    }

    async getAllWtFederations() {
        try {
            let res = await axios.post("/webservice_FFTRI/get_all_wt_federations.php", {});
            return res.data;
        } catch (err) {
            return false;
        }
    }

    async getChefEquipeRetraitDossard({ id_module, id_inscription } = {}) {
        if (!id_module || id_module == '') return false;
        if ((id_inscription === undefined || id_inscription === null || id_inscription === '')) {
            return false;
        }
        let params = '?id_module=' + id_module + '&id_inscription=' + id_inscription;

        try {
            let res = await axios.get(API_URL + 'creneaux_retrait_dossard/chef_equipe/get.php' + params);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }


}

export default new PublicService();